// Builds the plain object createTemplate/updateTemplate (src/api/templates.js) send to
// gs-intg-services POST/PUT /whatsapp/enterprise/:orgId/channel/:channelId/template[/:templateId].
//
// That route's body pipe is `new ValidationPipe({ whitelist: true, forbidNonWhitelisted: true })`
// (enterprise-template.controller.ts) and whitelisting cascades into every @ValidateNested() child
// too — a key the target DTO/sub-DTO doesn't declare is a 400, not a silently dropped field. Every
// object built below therefore lists only the exact keys its DTO class declares; nothing here is
// "extra for later".
//
// DTO keys with no driving field in this form's state shape (metadata, policy, wabaId, namespace,
// allowCategoryChange, callPermissionRequest, requestContactButton, newType) are intentionally
// never emitted — omitting an optional key is always safe, and there is nothing in state to
// derive them from. optOutButton is one exception (state.optOutButton, root-level — see
// state.js): driven, and covered below. quickReplyFirst is the other: not a field the user sets
// directly, but DERIVED from state.buttons' own order — see computeQuickReplyFirst below.

import {
  BUTTON_TYPE,
  HEADER_TYPE,
  MEDIA_TYPE,
  OTP_TYPE,
  MARKETING_OPT_OUT_BUTTON_TEXT,
  MARKETING_OPT_OUT_FOOTER,
  isAuthentication,
  isCarousel,
  isLto,
  isCallPermissionRequest,
  isCopyCode,
  isAutofill,
  isZeroTap,
  isPasskey,
  isMarketing,
  isLinkTracked,
  hasRequestContactInfo,
  quickRepliesFirst,
} from './state';

function omitEmpty(obj) {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value === undefined || value === null) return;
    result[key] = value;
  });
  return result;
}

// One CTA builder per CallToActionButtonType. Each returns exactly the keys its DTO class
// declares (url-button.dto.ts / phone-number-button.dto.ts / copy-code-button.dto.ts /
// flow-button.dto.ts / voice-call-button.dto.ts / order-details-button.dto.ts /
// request-contact-info-button.dto.ts) — never the internal state field names (buttonValue,
// suffix, hasDeepLink, activeFor, ...), which would trip forbidNonWhitelisted if leaked through.
const CTA_BUILDERS = {
  [BUTTON_TYPE.URL]: (btn) => {
    const isDynamic = btn.urlType.value === 'DYNAMIC';
    const isLinkTrack = isLinkTracked(btn);
    return omitEmpty({
      type: BUTTON_TYPE.URL,
      text: btn.text.value,
      urlType: btn.urlType.value,
      // WRITE side: send the base WITHOUT {{1}}. For urlType=DYNAMIC, create/update validation
      // requires example[0] to start with url and example[0] !== url (URLButton.java:143-161,
      // the startsWith check at :159-161) — appending {{1}} to url here would make that
      // startsWith check fail and the save would be rejected. Enterprise appends the variable
      // itself when building the outbound Meta payload (MessageTemplateService.java:2423:
      // callToActionButton.put(URL, url + "{{1}}")).
      // READ side is asymmetric: Meta returns url WITH "{{1}}" already appended, which is why
      // prefill.js's reconstructButton strips it back off into buttonValue/suffix. That's
      // correct and must stay — do not "fix" this asymmetry by appending {{1}} here again.
      //
      // DYNAMIC_LINK_TRACK: Enterprise ignores and overwrites both url and example with its own
      // link-tracking domain (URLButton.java:166-199) — send neither.
      url: isLinkTrack ? undefined : btn.buttonValue.value,
      example: isLinkTrack ? undefined : (isDynamic ? [`${btn.buttonValue.value || ''}${btn.suffix.value || ''}`] : undefined),
      // validate.js requires deepLink whenever hasDeepLink is true, so android_deep_link is never
      // actually blank by the time a real submit reaches here — the `|| undefined` on both mirrors
      // that same omitEmpty contract defensively. android_fallback_playstore_url is genuinely
      // optional and must never ride as '' when the user left it blank.
      app_deep_link: btn.hasDeepLink
        ? omitEmpty({
          android_deep_link: btn.deepLink.value || undefined,
          android_fallback_playstore_url: btn.fallbackLink.value || undefined,
        })
        : undefined,
    });
  },
  [BUTTON_TYPE.PHONE_NUMBER]: (btn) => omitEmpty({
    type: BUTTON_TYPE.PHONE_NUMBER,
    text: btn.text.value,
    phone_number: btn.buttonValue.value,
  }),
  // example is a plain STRING here — every other button type's example is an array. Sending an
  // array for COPY_CODE (or a string for URL) is the single most common cause of a rejected create.
  // No `text` key: the console locks the displayed label to COPY_CODE_BUTTON_LABEL and ce-api's
  // CopyOfferCodeButton.validate() never reads text at all (only `example`, the code) — so this
  // form never sends it, same as PASSKEY hardcoding its own text below rather than reading state.
  [BUTTON_TYPE.COPY_CODE]: (btn) => omitEmpty({
    type: BUTTON_TYPE.COPY_CODE,
    example: btn.buttonValue.value,
  }),
  [BUTTON_TYPE.FLOW]: (btn) => omitEmpty({
    type: BUTTON_TYPE.FLOW,
    text: btn.text.value,
    flow_id: Number(btn.flowId.value),
    flow_action: btn.flowAction.value || undefined,
    navigate_screen: btn.buttonValue.value || undefined,
  }),
  [BUTTON_TYPE.VOICE_CALL]: (btn) => omitEmpty({
    type: BUTTON_TYPE.VOICE_CALL,
    text: btn.text.value,
    // Console-mirrored: state stores days, wire wants minutes (1440-43200, i.e. 1-30 days).
    ttl_minutes: btn.activeFor.value ? Number(btn.activeFor.value) * 24 * 60 : undefined,
  }),
  [BUTTON_TYPE.ORDER_DETAILS]: (btn) => omitEmpty({
    type: BUTTON_TYPE.ORDER_DETAILS,
    text: btn.text.value || undefined,
  }),
  [BUTTON_TYPE.REQUEST_CONTACT_INFO]: (btn) => omitEmpty({
    type: BUTTON_TYPE.REQUEST_CONTACT_INFO,
    text: btn.text.value || undefined,
  }),
};

function buildOtpButton(btn, state) {
  let otpType = OTP_TYPE.NO_BUTTONS;
  if (isCopyCode(state)) otpType = OTP_TYPE.COPY_CODE;
  else if (isAutofill(state)) otpType = OTP_TYPE.ONE_TAP;
  else if (isZeroTap(state)) otpType = OTP_TYPE.ZERO_TAP;

  const needsAutofill = isAutofill(state) || isZeroTap(state);
  return omitEmpty({
    type: BUTTON_TYPE.OTP,
    otp_type: otpType,
    text: btn.text.value || undefined,
    autofill_text: needsAutofill ? (btn.autofillText.value || undefined) : undefined,
    zero_tap_terms_accepted: isZeroTap(state) ? Boolean(btn.zeroTapTermsAccepted) : undefined,
    supported_apps: (needsAutofill && state.appsSetup.length)
      ? state.appsSetup.map((app) => ({
        package_name: app.packageName.value,
        signature_hash: app.signatureHash.value,
      }))
      : undefined,
  });
}

// The DTO only accepts the literal 'Verify' (IsIn(['Verify'])) — sent unconditionally, same as
// the console's hardcoded PASSKEY_BUTTON_TEXT, regardless of any button text in state.
function buildPasskeyButton() {
  return { type: BUTTON_TYPE.PASSKEY, text: 'Verify' };
}

// ce-api builds a template's components as CTA-group-then-quick-replies by default and only
// flips that order when quickReplyFirst is true (HsmJson.java's prepareComponents) — so a
// template authored quick-reply-first ships CTA-first unless we say otherwise. Layers one extra
// gate on top of state.js's own quickRepliesFirst(state) — the same group-order rule
// sections/Buttons.js renders by and Form/index.js's buildPreviewTemplate lists by, so all three
// can never disagree about which group is "first": true only when quickRepliesFirst(state) says
// quick-reply-first AND at least one CTA button exists elsewhere in the list. Both conditions
// matter: a quick-reply-only template has nothing for quickReplyFirst to reorder relative to, and
// gs-ui-templates (the console) never emits this key at all — there is no console behaviour to
// mirror here, so this is new ground, not a parity gap.
//
// The Marketing opt-out reply is never in state.buttons to begin with (state.js — it's appended
// by bucketButtons below, always last), so reading state.buttons directly already excludes it
// from "first button" without any extra filtering.
function computeQuickReplyFirst(state) {
  if (!quickRepliesFirst(state)) return undefined;
  const hasCta = state.buttons.some((btn) => Boolean(CTA_BUILDERS[btn.type]));
  return hasCta ? true : undefined;
}

// Splits the one flat buttons[] list into the four DTO-shaped destinations. Exported so
// Form/index.js's buildPreviewTemplate can build the live/submit-preview's containerMeta.buttons
// from the exact same bucketing logic the real submit payload uses, instead of a second
// hand-rolled copy that could drift from this one.
export function bucketButtons(state) {
  const cta = [];
  const quickReply = [];
  let otp;
  let passkey;
  state.buttons.forEach((btn) => {
    if (btn.type === BUTTON_TYPE.QUICK_REPLY) {
      quickReply.push(btn.text.value);
      return;
    }
    if (btn.type === BUTTON_TYPE.OTP) {
      otp = buildOtpButton(btn, state);
      return;
    }
    if (btn.type === BUTTON_TYPE.PASSKEY) {
      passkey = buildPasskeyButton();
      return;
    }
    const build = CTA_BUILDERS[btn.type];
    if (build) cta.push(build(btn));
  });

  // Marketing opt-out: Enterprise overwrites the LAST quick reply's text with
  // MARKETING_OPT_OUT_BUTTON_TEXT server-side regardless of what we send there
  // (MessageTemplate.java:1679-1682) — appending our own copy of that exact text keeps the wire
  // array self-consistent with what Enterprise will actually show, rather than relying on a
  // server-side overwrite the client never sees. Folded into bucketButtons (not buildPayload alone)
  // so Form/index.js's buildPreviewTemplate — which calls this same function to build the live
  // preview — shows the opt-out reply too, instead of drifting from the real submit payload. Always
  // appended last, never replaces a user-authored entry. isMarketing() gate matches buildPayload's
  // own optOutButton/footer gating below — a stray flag left over from switching category away
  // from Marketing must stay inert everywhere, not just in the JSON that gets sent.
  // Call Permission Request sends no buttons at all (Buttons.js hides its whole section for it,
  // same as it does for Carousel/Authentication) — guarded here too so a stale optOutButton flag
  // left over from switching subcategory away from another Marketing type can't append one anyway.
  // Request Contact Info rejects any quick reply outright (ValidationUtils.
  // validateRequestContactInfoTemplate) — same guard, same reasoning.
  if (isMarketing(state) && state.optOutButton && !isCallPermissionRequest(state) && !hasRequestContactInfo(state)) {
    quickReply.push(MARKETING_OPT_OUT_BUTTON_TEXT);
  }

  return { cta, quickReply, otp, passkey };
}

// CarouselCardButtonDto is a flatter, narrower shape than CallToActionButtonDto (no
// app_deep_link, no flow_id/ttl_minutes) — built directly rather than reusing CTA_BUILDERS, which
// would leak disallowed keys.
function buildCarouselCardButton(btn) {
  const isUrl = btn.type === BUTTON_TYPE.URL;
  const isDynamic = isUrl && btn.urlType?.value === 'DYNAMIC';
  const isLinkTrack = isUrl && isLinkTracked(btn);
  return omitEmpty({
    type: btn.type,
    text: btn.text.value || undefined,
    urlType: isUrl ? btn.urlType.value : undefined,
    // Same write-side contract as CTA_BUILDERS[URL] above: send the base WITHOUT {{1}}. A
    // carousel card's url button goes through the identical URLButton.validate() startsWith
    // check (ValidationUtils.java:1063-1068 runs it for card buttons too, same rule as
    // URLButton.java:159-161), and Enterprise appends {{1}} itself on the way to Meta
    // (MessageTemplateService.java:2423). READ side still comes back with {{1}} already
    // appended, stripped by prefill.js — see CTA_BUILDERS[URL]'s comment. Don't re-append it here.
    //
    // DYNAMIC_LINK_TRACK: same as CTA_BUILDERS[URL] — Enterprise fills url/example itself
    // (URLButton.java:166-199) — send neither.
    url: (isUrl && !isLinkTrack) ? btn.buttonValue.value : undefined,
    example: isLinkTrack ? undefined : (isDynamic ? [`${btn.buttonValue.value || ''}${btn.suffix.value || ''}`] : undefined),
    phone_number: btn.type === BUTTON_TYPE.PHONE_NUMBER ? btn.buttonValue.value : undefined,
  });
}

// Exported so Form/index.js's buildPreviewTemplate can reuse this for the preview's carousel
// cards. Only the `buttons` sub-shape is actually preview-compatible (see the caller) — the
// export itself doesn't change what this function returns.
export function buildCarouselCard(card) {
  return {
    body: omitEmpty({
      text: card.description.value,
      example: card.variable.length ? card.variable.map((v) => v.value) : undefined,
    }),
    header: { id: card.cardMedia.mediaId },
    buttons: card.buttons.map(buildCarouselCardButton),
  };
}

// Single source of truth for what footer text the payload actually sends. Form/index.js's live
// preview bubble and the preview modal's summary row both call this too, instead of each keeping
// its own drifted copy — the preview bubble was missing the `!carousel` guard below, and the
// summary row read the raw footer field even while opt-out was about to silently overwrite it,
// so it showed "—" for an opt-out template that was actually about to send a footer. Opt-out
// overwrites the footer unconditionally server-side too (MessageTemplate.java:1575-1577), but we
// send the real value ourselves rather than relying on that — self-consistent wire payload,
// accurate preview, same reasoning as bucketButtons' quick-reply append above. Carousel and Call
// Permission Request are Marketing too but have no opt-out UI — guarded here so a stale/prefilled
// flag can't fire it for either (Category.js resets the flag on a live switch to CPR, but a row
// prefilled straight into CPR with the flag already set — e.g. from prefill.js's own
// last-quick-reply-reads-"Stop Promotions" heuristic — never goes through that reset).
// Returns undefined (never '') when there's no footer, matching buildPayload's own omitEmpty
// contract, so callers that need a display fallback do `effectiveFooter(state) || <fallback>`.
export function effectiveFooter(state) {
  // Auth (other than Passkey) never sends a footer at all — Enterprise builds its own from
  // codeExpirationMinutes (config.tpl's EXPIRE_IN_MINUTE) and rejects a caller-supplied one the
  // same way it rejects a caller-supplied body (see buildPayload's own template/body comment).
  // Passkey keeps the normal footer behaviour below — it's the one auth sub-type with a real,
  // user-editable footer (Footer.js).
  if (isAuthentication(state) && !isPasskey(state)) return undefined;
  const optOutActive = isMarketing(state) && state.optOutButton && !isCarousel(state)
    && !isCallPermissionRequest(state) && !hasRequestContactInfo(state);
  return optOutActive ? MARKETING_OPT_OUT_FOOTER : (state.footer.footerText.value || undefined);
}

export function buildPayload(state, { isEdit = false } = {}) {
  const { cta, quickReply, otp, passkey } = bucketButtons(state);
  const carousel = isCarousel(state);
  const isTextHeader = state.header.value === HEADER_TYPE.TEXT;
  const isMediaHeader = state.header.value === HEADER_TYPE.MEDIA;
  // Location has no sample media at all (state contract: header.value = MEDIA, headerMedia.value =
  // 'LOCATION', never an upload — see state.js). ce-api's validateMediaSample skips LOCATION
  // outright (MessageTemplate.java:2287's type switch only lists IMAGE/VIDEO/DOCUMENT/GIF), so
  // sending a mediaUrl for it would be sending a field Enterprise never asked for. headerMedia.
  // mediaSrc should already be '' for LOCATION in every real case (nothing ever populates it — see
  // prefill.js), but this is checked explicitly rather than relying on that, so a future prefill/UI
  // change can't silently start leaking one through.
  const isLocationHeader = isMediaHeader && state.header.headerMedia.value === MEDIA_TYPE.LOCATION;
  // Carousel, Call Permission Request and Request Contact Info are Marketing too but have no
  // opt-out UI — guard so a stale/prefilled flag can't fire it for any of them (see
  // effectiveFooter's own comment above).
  const optOutActive = isMarketing(state) && state.optOutButton && !carousel
    && !isCallPermissionRequest(state) && !hasRequestContactInfo(state);

  const payload = {
    // Required on both create and update.
    category: state.category.value,
    type: state.type.value,
    template: state.body.value,

    // UpdateTemplateDto rejects both outright — upstream overwrites them from the live Meta
    // template anyway, so create-only.
    ...(!isEdit && {
      templateName: state.templateName.value,
      language: state.language.value,
    }),

    // subcategory is deliberately NEVER emitted, on create or edit — do not re-add it.
    // MessageTemplate.java:1358-1360 only validates subcategory when it's present
    // (`if (StringUtils.isNotBlank(this.subcategory)) { ValidationUtils.validateSubCategory(...) }`),
    // so omitting the key is valid. Sending it is not: validateSubCategory checks the value against
    // a runtime-reloadable property (whatsapp.hsm.allowed.marketing/utility.subcategories) whose
    // accepted vocabulary is environment config and doesn't match this UI's selectedSubcategory
    // values (e.g. the server wants ORDER_DETAIL_MESSAGE, not ORDER_DETAILS) — and for
    // AUTHENTICATION any non-blank subcategory throws unconditionally. A real create against dev
    // with subcategory sent 400'd: "The subcategory 'MARKETING' is not yet supported." The
    // console's own createTemplatePayload never sends this key either.
    // selectedSubcategory stays a UI-only concept that only decides which form sections render
    // (see isCarousel/isLto/isFlow/isOrderDetails/isCopyCode/isAutofill/isZeroTap/isPasskey below)
    // and, for OTP buttons, which otp_type to send (a different, closed DTO enum on a different
    // field — safe, see buildOtpButton) — it must never be forwarded onto the wire as `subcategory`.

    templateVariableExamples: state.body.variable.length
      ? state.body.variable.map((v) => v.value)
      : undefined,

    header: isTextHeader ? state.header.headerText.value : undefined,
    headerExamples: (isTextHeader && state.header.headerText.variable.length)
      ? [state.header.headerText.variable[0].value]
      : undefined,
    // Only meaningful for an already-hosted media (mediaSrc from a prior upload) — a fresh local
    // `file` has no real URL yet, and @IsUrl() would reject a blob: preview URL anyway. Excluded
    // outright for LOCATION — see isLocationHeader above.
    mediaUrl: (isMediaHeader && !isLocationHeader && state.header.headerMedia.mediaSrc && !state.header.headerMedia.file)
      ? state.header.headerMedia.mediaSrc
      : undefined,

    // See effectiveFooter above — shared with Form/index.js's preview bubble and summary row.
    footer: effectiveFooter(state),

    // Carousel sends buttons per card (below), never top-level — guard even if state.buttons is stale.
    quickReplyButtons: (!carousel && quickReply.length) ? quickReply : undefined,
    callToActionButtons: (!carousel && cta.length) ? cta : undefined,
    // See computeQuickReplyFirst above. Same !carousel guard as the two arrays immediately above —
    // a carousel template's buttons live per card, not in this top-level pair, so there's nothing
    // here for quickReplyFirst to reorder.
    quickReplyFirst: !carousel ? computeQuickReplyFirst(state) : undefined,
    otpButtons: otp ? [otp] : undefined,
    passkeyButtons: passkey ? [passkey] : undefined,

    // Never sent as `false` — omitEmpty only strips null/undefined, and the DTO only ever checks
    // this key when it's `true` (QuickReplyRequiredForOptOut validator), so an explicit false would
    // be a no-op at best. A non-Marketing template ignores state.optOutButton even if it's somehow
    // true, same gate as the quick-reply append above.
    optOutButton: optOutActive ? true : undefined,

    carouselCards: (carousel && state.carousels.cards.length)
      ? state.carousels.cards.map(buildCarouselCard)
      : undefined,
    carouselMediaType: carousel ? state.carousels.mediaType : undefined,

    limitedTimeOffer: isLto(state)
      ? { text: state.lto.offerTitle.value, has_expiration: state.lto.isExpiryChecked }
      : undefined,

    // Call Permission Request — a Marketing/Utility Template Type with a TEXT-only header and no
    // buttons (see Header.js/Category.js/Buttons.js). Sent only for it; every other template omits
    // the key entirely, matching the DTO's own optional field (gs-intg template-content.dto.ts:
    // 161-164, mapped to call_permission_request for both create and update).
    callPermissionRequest: isCallPermissionRequest(state) ? true : undefined,

    // !isPasskey(state) guard is defensive, not just a mirror of AuthMessageContent.js hiding the
    // checkboxes that set these: AuthTemplateButtonRules (gs-intg dto/validators/
    // auth-template-buttons.validator.ts:36-43) forbids both keys outright for a passkey
    // template, and messageContent isn't reset on a subcategory change (Category.js never
    // touches it), so a flag set before switching subcategory TO Passkey must not ride along.
    codeExpirationMinutes: (isAuthentication(state) && !isPasskey(state) && state.messageContent.isExpirationTimeChecked)
      ? Number(state.messageContent.expirationTime.value)
      : undefined,
    addSecurityRecommendation: (isAuthentication(state) && !isPasskey(state) && state.messageContent.isSecurityCheck)
      ? true
      : undefined,

    messageSendTtlSeconds: state.messageValidity.isValidityChecked
      ? Number(state.messageValidity.validityPeriod.value) * 60
      : undefined,

    ...(isEdit && { status: state.status.value }),
  };

  return omitEmpty(payload);
}
